import { useEffect, useState } from 'react';

import { APOLLO_CLIENTS, CustomContext } from '@/graphql/client';
import type {
  OrderAppCtaType,
  OrderAppProductVariantsBusinessDetailsQuery,
} from '@/graphql/orderApp/api';
import { useProductVariantsBusinessDetailsLazyQuery } from '@/graphql/orderApp/api';
import type {
  StrapAddToBagProductsQuery,
  StrapAddToBagToolsQuery,
  StrapAttributesFragment,
} from '@/graphql/saleor/api';
import { useStrapAddToBagProductsQuery } from '@/graphql/saleor/api';
import { getMultiselectValue } from '@/lib/attributes';
import { NEXT_PUBLIC_IMAGES_FORMAT, THUMBNAIL_IMAGE_SIZE } from '@/lib/const';
import { isAbortLikeError } from '@/lib/errors';
import { mapEdgesToItems } from '@/lib/maps';
import { fetchTools } from '@/lib/product';
import { useRegionProvider } from '@/providers/RegionProvider';

export type StrapAddToBagBuckle = NonNullable<
  StrapAddToBagProductsQuery['productVariants']
>['edges'][number]['node'];

export type StrapAddToBagTool = NonNullable<
  NonNullable<
    StrapAddToBagToolsQuery['products']
  >['edges'][number]['node']['defaultVariant']
>;

const EMPTY_BUSINESS_DATA = {
  productVariants: [],
} as OrderAppProductVariantsBusinessDetailsQuery;

type FetchBucklesResult =
  | { aborted: false; buckles: StrapAddToBagBuckle[] }
  | { aborted: true; buckles: [] };

type FetchBusinessDataFn = ReturnType<
  typeof useProductVariantsBusinessDetailsLazyQuery
>[0];

const safeFetchBuckles = async (
  fetchBuckles: (options: {
    filter: { sku: string[] };
  }) => Promise<{ data: StrapAddToBagProductsQuery }>,
  skus: string[],
): Promise<FetchBucklesResult> => {
  try {
    const { data } = await fetchBuckles({ filter: { sku: skus } });
    return { aborted: false, buckles: mapEdgesToItems(data.productVariants) };
  } catch (err) {
    if (isAbortLikeError(err)) {
      return { aborted: true, buckles: [] };
    }
    console.warn('[useBucklesAndTools] fetchBuckles failed:', err);
    return { aborted: false, buckles: [] };
  }
};

const safeFetchBusinessData = async (
  fetchBusinessData: FetchBusinessDataFn,
  skus: string[],
  channel: string,
): Promise<OrderAppProductVariantsBusinessDetailsQuery> => {
  try {
    const res = await fetchBusinessData({ variables: { skus, channel } });
    return (
      (res?.data as OrderAppProductVariantsBusinessDetailsQuery) ??
      EMPTY_BUSINESS_DATA
    );
  } catch (err) {
    if (!isAbortLikeError(err)) {
      console.warn('[useBucklesAndTools] fetchBusinessData failed:', err);
    }
    return EMPTY_BUSINESS_DATA;
  }
};

const filterBucklesByCta = (
  buckles: StrapAddToBagBuckle[],
  businessData: OrderAppProductVariantsBusinessDetailsQuery,
  ctaType: OrderAppCtaType,
): StrapAddToBagBuckle[] =>
  buckles.filter((item) => {
    const cta = businessData.productVariants?.find(
      (jtem: { sku?: string | null }) => jtem?.sku === item.sku,
    ) as { variantCTA?: OrderAppCtaType } | undefined;
    return cta?.variantCTA === ctaType;
  });

const safeFetchTools = async (
  query: ReturnType<typeof useRegionProvider>['query'],
): Promise<StrapAddToBagTool[]> => {
  try {
    return await fetchTools(query);
  } catch (err) {
    if (!isAbortLikeError(err)) {
      console.warn('[useBucklesAndTools] fetchTools failed:', err);
    }
    return [];
  }
};

export const useBucklesAndTools = (
  compatibleBuckles: StrapAttributesFragment['compatibleBuckles'],
  orderCtaType?: OrderAppCtaType | null,
  lazy?: boolean,
) => {
  const [buckles, setBuckles] = useState<StrapAddToBagBuckle[] | null>(null);
  const [tools, setTools] = useState<StrapAddToBagTool[] | null>(null);
  const [loading, setLoading] = useState(false);

  const { query } = useRegionProvider();

  const { refetch: fetchBuckles } = useStrapAddToBagProductsQuery({
    variables: {
      limit: 50,
      thumbnailSize: THUMBNAIL_IMAGE_SIZE,
      imagesFormat: NEXT_PUBLIC_IMAGES_FORMAT,
      ...query,
    },
    skip: true,
    fetchPolicy: 'cache-first',
  });

  const compatibleBucklesReferences = getMultiselectValue(compatibleBuckles);
  const [fetchBusinessData] = useProductVariantsBusinessDetailsLazyQuery({
    variables: {
      skus: compatibleBucklesReferences,
      channel: query.channel,
    },
    context: CustomContext({
      client: APOLLO_CLIENTS.orderApp,
      disableErrorNotification: true,
    }),
    fetchPolicy: 'network-only',
  });

  const update: (
    initialCompatibleBuckles?: StrapAttributesFragment['compatibleBuckles'],
  ) => Promise<
    | {
        buckles: StrapAddToBagBuckle[];
        tools: StrapAddToBagTool[];
      }
    | undefined
  > = async (initialCompatibleBuckles) => {
    const skus = getMultiselectValue(
      initialCompatibleBuckles ?? compatibleBuckles,
    );
    setLoading(true);

    try {
      const buckleProducts = await getBuckleProducts(skus);
      if (buckleProducts === undefined) {
        return;
      }

      if (buckleProducts.length) {
        setBuckles(buckleProducts);
      }

      const toolProducts = await safeFetchTools(query);
      if (toolProducts.length) {
        setTools(toolProducts);
      }

      return { buckles: buckleProducts, tools: toolProducts };
    } finally {
      setLoading(false);
    }
  };

  const getBuckleProducts = async (
    skus: string[],
  ): Promise<StrapAddToBagBuckle[] | undefined> => {
    if (!skus.length) {
      return [];
    }

    const result = await safeFetchBuckles(fetchBuckles, skus);
    if (result.aborted) {
      return undefined;
    }

    if (!orderCtaType) {
      return result.buckles;
    }

    const businessData = await safeFetchBusinessData(
      fetchBusinessData,
      skus,
      query.channel,
    );
    return filterBucklesByCta(result.buckles, businessData, orderCtaType);
  };

  useEffect(() => {
    if (lazy) return;
    update();
  }, [compatibleBuckles, lazy]);

  return { buckles, tools, loading, update };
};
